// ─────────────────────────────────────────────────────────────────────────────
// SEÇÃO "SOLUÇÕES PARA PROPRIETÁRIOS" — conteúdo + ALLOWLIST por codsite.
//
// Ticket #1456 (Unio Imóveis, cs2451): bloco com 3 caminhos para quem TEM imóvel
// (avaliar / vender / alugar com administração), logo abaixo do buscador da home.
//
// ⚠️ Este é o código dos ~300 sites: o pedido é de UM cliente, então a seção mora
// numa allowlist, no padrão de `utils/site2524WhatsApp.ts`. Um segundo cliente é
// uma ENTRADA NOVA neste mapa, não um `if` novo espalhado pelo render.
//
// A seção nasce DESLIGADA: quem não está no mapa recebe `null` e não renderiza
// nada (nem wrapper vazio). Sem teste `!= 0` — `Record[NaN]`/`Record[0]` já é
// `undefined`, então tenant sem entrada nunca liga por acidente.
// ─────────────────────────────────────────────────────────────────────────────
import type { StaticImageData } from 'next/image';

import imgAvaliacao from '../assets/images/proprietarios-2451/avaliacao.webp';
import imgAdministracao from '../assets/images/proprietarios-2451/administracao.webp';
import imgVenda from '../assets/images/proprietarios-2451/venda.webp';

export type ProprietarioCard = {
    /** Identificador de rastreio pedido pelo cliente: vira `id`, `data-track-id` e `card_id` no dataLayer. */
    id: string;
    titulo: string;
    texto: string;
    cta: string;
    /** URL absoluta e LIMPA — sem `_gl`/`_ga`/UTM (exigência do item 5 do ticket #1456). */
    href: string;
    img: StaticImageData;
    alt: string;
};

export type HomeProprietariosConfig = {
    eyebrow: string;
    titulo: string;
    apoio: string;
    cards: ProprietarioCard[];
};

export const HOME_PROPRIETARIOS_BY_SITE: Record<number, HomeProprietariosConfig> = {
    // cs2451 — Unio Imóveis (unioimoveis.com.br). Textos e links conferidos com o
    // ticket #1456; as landing pages são do próprio cliente (mesma aba, item 5).
    2451: {
        eyebrow: 'SOLUÇÕES PARA PROPRIETÁRIOS',
        titulo: 'Você é proprietário?',
        apoio:
            'Quer vender, alugar ou saber quanto vale seu imóvel? Conte com avaliação profissional, ' +
            'divulgação estratégica e gestão completa do seu patrimônio.',
        cards: [
            {
                id: 'home_proprietario_avaliacao',
                titulo: 'Avalie seu imóvel',
                texto: 'Descubra o valor adequado para venda ou locação com uma avaliação profissional.',
                cta: 'Solicitar avaliação gratuita',
                href: 'https://unioimoveislp.unioimoveis.com.br/avaliacao-imobiliaria',
                img: imgAvaliacao,
                alt: 'Profissional realizando a avaliação de um imóvel',
            },
            {
                id: 'home_proprietario_venda',
                titulo: 'Venda com a Unio',
                texto: 'Divulgação estratégica, atendimento aos interessados e acompanhamento completo da negociação.',
                cta: 'Quero vender meu imóvel',
                href: 'https://unioimoveislp.unioimoveis.com.br/vendaimovelunio',
                img: imgVenda,
                alt: 'Produção fotográfica profissional para venda de imóvel',
            },
            {
                id: 'home_proprietario_administracao',
                titulo: 'Alugue sem preocupação',
                texto: 'Da escolha do inquilino ao repasse do aluguel: cuidamos da gestão e da segurança do seu patrimônio.',
                cta: 'Conhecer a administração',
                href: 'https://unioimoveislp.unioimoveis.com.br/adm-locacao',
                img: imgAdministracao,
                alt: 'Proprietária acompanhando a administração profissional do imóvel',
            },
        ],
    },
};

/** Config da seção para o tenant, ou `null` quando ele não está na allowlist. */
export const getHomeProprietarios = (site: any): HomeProprietariosConfig | null =>
    HOME_PROPRIETARIOS_BY_SITE[Number(site?.id)] ?? null;
